import { useEffect, useRef, useState } from 'react';
import { IconFileDownload, IconX, IconChevronDown, IconSearch } from '@tabler/icons-react';
import { months, getAvailableYears } from '../../data/timesheetData';

const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
];

const filterSelectClass = 'w-full bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] text-sm';

function PillRow({ options, value, onChange, activeClass }) {
    return (
        <div className='flex gap-6'>
            {options.map(opt => (
                <button
                    key={opt.value}
                    type='button'
                    onClick={() => onChange(opt.value)}
                    className={`flex-1 py-5 rounded text-xs font-semibold transition-colors ${value === opt.value ? activeClass : 'bg-bg-light text-text-secondary hover:bg-org-blue-primary/10'
                        }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}

function EmployeeSearchSelect({ employees, value, onChange, allOptionLabel, selfOptionLabel, currentEmployeeId }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const allOptions = [
        { id: '', name: allOptionLabel },
        ...(currentEmployeeId ? [{ id: currentEmployeeId, name: selfOptionLabel }] : []),
        ...employees.map(emp => ({ id: emp.id, name: emp.name })),
    ];

    const filtered = allOptions.filter(o => o.name?.toLowerCase().includes(search.toLowerCase()));
    const selected = allOptions.find(o => o.id === value);

    return (
        <div className='relative' ref={ref}>
            <button
                type='button'
                onClick={() => setOpen(o => !o)}
                className={`${filterSelectClass} text-left flex items-center justify-between`}
            >
                <span className='truncate'>{selected ? selected.name : allOptionLabel}</span>
                <IconChevronDown size={12} className={`flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className='relative mt-2 w-full bg-bg-dark border border-border rounded-lg shadow-lg overflow-hidden'>
                    <div className='p-6 border-b border-border'>
                        <div className='relative'>
                            <IconSearch size={14} className='absolute left-10 top-1/2 -translate-y-1/2 text-text-secondary' />
                            <input
                                autoFocus
                                type='text'
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder='Search employee...'
                                className='w-full bg-bg-light border border-border py-[6px] pl-30 pr-10 rounded-[4px] text-sm outline-none'
                            />
                        </div>
                    </div>
                    <div className='max-h-[180px] overflow-y-auto'>
                        {filtered.length > 0 ? filtered.map(o => (
                            <div
                                key={o.id || 'all'}
                                onMouseDown={(e) => { e.preventDefault(); onChange(o.id); setOpen(false); setSearch(''); }}
                                className={`px-12 py-[7px] text-xs cursor-pointer transition-colors ${value === o.id ? 'bg-org-blue-primary/10 text-org-blue-primary' : 'hover:bg-bg-light'}`}
                            >
                                {o.name}
                            </div>
                        )) : (
                            <div className='px-12 py-6 text-center text-text-secondary text-xs'>No employees found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function MonthGrid({ value, onChange }) {
    return (
        <div className='grid grid-cols-3 gap-5'>
            {months.map((m, idx) => (
                <button
                    key={m}
                    type='button'
                    onClick={() => onChange(idx + 1)}
                    className={`py-5 px-6 rounded text-[11px] font-semibold transition-colors ${value === idx + 1
                        ? 'bg-org-green-primary text-white'
                        : 'bg-bg-light text-text-secondary hover:bg-org-green-primary/10'
                        }`}
                >
                    {m.slice(0, 3)}
                </button>
            ))}
        </div>
    );
}

export default function ReportDownloadButton({
    label = 'Download Report',
    orgId,
    employees = [],
    canPickEmployee = false,
    canFilterStatus = false,
    currentEmployeeId,
    allOptionLabel = 'Whole Organization',
    selfOptionLabel = 'My Own Data',
    onDownload,
    isPending = false,
}) {
    const now = new Date();
    const yearOptions = getAvailableYears().map(y => ({ value: y, label: String(y) }));
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('month');
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [startYear, setStartYear] = useState(now.getFullYear());
    const [startMonth, setStartMonth] = useState(now.getMonth() + 1);
    const [endYear, setEndYear] = useState(now.getFullYear());
    const [endMonth, setEndMonth] = useState(now.getMonth() + 1);
    const [employeeId, setEmployeeId] = useState('');
    const [status, setStatus] = useState('');

    const handleDownload = () => {
        const params = mode === 'range'
            ? { mode, startYear, startMonth, endYear, endMonth, orgId }
            : { mode, year, month, orgId };
        if (canPickEmployee) {
            if (employeeId) params.employeeId = employeeId;
        } else if (currentEmployeeId) {
            params.employeeId = currentEmployeeId;
        }
        if (canFilterStatus && status) params.status = status;
        onDownload(params);
        setOpen(false);
    };

    return (
        <div className='relative'>
            <button
                type='button'
                onClick={() => setOpen(true)}
                className='flex items-center gap-2 bg-org-blue-primary text-white px-6 py-3 rounded text-[11px] font-medium hover:opacity-90'
            >
                <IconFileDownload size={14} />
                {label}
            </button>

            {open && (
                <>
                <div className='fixed p-15 w-full max-w-[480px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]'>
                <div className='bg-bg-dark rounded-lg shadow-light max-h-[85vh] overflow-y-auto p-12'>
                    <div className='flex items-center justify-between mb-10'>
                        <h5 className='text-[14px] font-medium'>{label}</h5>
                        <button type='button' onClick={() => setOpen(false)} className='text-text-secondary hover:text-text-primary'>
                            <IconX size={16} />
                        </button>
                    </div>
                    <div className='mb-10'>
                        <p className='text-[10px] text-text-secondary font-semibold uppercase mb-6'>Period</p>
                        <PillRow
                            options={[{ value: 'month', label: 'Month' }, { value: 'range', label: 'Range' }]}
                            value={mode}
                            onChange={setMode}
                            activeClass='bg-org-blue-primary text-white'
                        />
                    </div>

                    {mode === 'month' ? (
                        <>
                            <div className='mb-10'>
                                <p className='text-[10px] text-text-secondary font-semibold uppercase mb-6'>Year</p>
                                <PillRow
                                    options={yearOptions}
                                    value={year}
                                    onChange={setYear}
                                    activeClass='bg-org-blue-primary text-white'
                                />
                            </div>
                            <div className='mb-10'>
                                <p className='text-[10px] text-text-secondary font-semibold uppercase mb-6'>Month</p>
                                <MonthGrid value={month} onChange={setMonth} />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className='mb-10'>
                                <p className='text-[10px] text-text-secondary font-semibold uppercase mb-6'>From</p>
                                <div className='grid grid-cols-2 gap-6'>
                                    <select value={startYear} onChange={e => setStartYear(Number(e.target.value))} className={filterSelectClass}>
                                        {yearOptions.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                                    </select>
                                    <select value={startMonth} onChange={e => setStartMonth(Number(e.target.value))} className={filterSelectClass}>
                                        {months.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className='mb-10'>
                                <p className='text-[10px] text-text-secondary font-semibold uppercase mb-6'>To</p>
                                <div className='grid grid-cols-2 gap-6'>
                                    <select value={endYear} onChange={e => setEndYear(Number(e.target.value))} className={filterSelectClass}>
                                        {yearOptions.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                                    </select>
                                    <select value={endMonth} onChange={e => setEndMonth(Number(e.target.value))} className={filterSelectClass}>
                                        {months.map((m, idx) => <option key={m} value={idx + 1}>{m}</option>)}
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    {canPickEmployee && (
                        <div className='mb-10'>
                            <p className='text-[10px] text-text-secondary font-semibold uppercase mb-6'>Employee</p>
                            <EmployeeSearchSelect
                                employees={employees}
                                value={employeeId}
                                onChange={setEmployeeId}
                                allOptionLabel={allOptionLabel}
                                selfOptionLabel={selfOptionLabel}
                                currentEmployeeId={currentEmployeeId}
                            />
                        </div>
                    )}

                    {canFilterStatus && (
                        <div className='mb-10'>
                            <p className='text-[10px] text-text-secondary font-semibold uppercase mb-6'>Status</p>
                            <select
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                                className={filterSelectClass}
                            >
                                {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                        </div>
                    )}

                    <button
                        type='button'
                        onClick={handleDownload}
                        disabled={isPending}
                        className='w-full flex items-center justify-center gap-2 bg-org-blue-primary text-white px-6 py-3 rounded text-[11px] font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        <IconFileDownload size={14} />
                        {isPending ? 'Downloading...' : 'Download'}
                    </button>
                </div>
                </div>
                <div onClick={() => setOpen(false)} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
                </>
            )}
        </div>
    );
}
